import {
    notEmpty, lengthAtLeast, lengthAtMost,
    validStreetAddress, validState, validCity,
    validPostalCode, validUSPostalCode, validCAPostalCode,
    validGBPostalCode, optionalRule
} from './rules';

const specificPostalCode = ['CA', 'GB', 'US'];
const requiresPostalCode = [
    'DZ', 'AR', 'AM', 'AU', 'AT', 'AZ', 'BD', 'BY', 'BE', 'BA', 'BR', 'BN', 'BG',
    'CA', 'CN', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FO', 'FI', 'FR', 'GE', 'DE', 'GR',
    'GL', 'GU', 'HU', 'IN', 'ID', 'IL', 'IT', 'JP', 'KZ', 'KG', 'LV', 'LI', 'LT',
    'LU', 'MK', 'MG', 'MY', 'MH', 'MQ', 'YT', 'MX', 'MN', 'ME', 'NL', 'NZ', 'NO',
    'PK', 'PH', 'PL', 'PT', 'PR', 'RE', 'RU', 'SA', 'RS', 'SG', 'SK', 'SI', 'ZA',
    'KR', 'ES', 'LK', 'BL', 'SE', 'CH', 'TW', 'TJ', 'TH', 'TN', 'TR', 'TM', 'VI',
    'UA', 'GB', 'US', 'UY', 'UZ', 'VA', 'VN'
];
const requiresState = ['AU', 'BR', 'CA', 'JP', 'US'];

// We need this for IE 11, since it does not support
// the syntax of new Set([...])
const buildSet = (list) => {
    const result = new Set();
    list.forEach((x) => { result.add(x); });
    return result;
};

export const getVrboRules = (country, latinOnly) => {
    const specificPostalCodeSet = buildSet(specificPostalCode);
    const requiresPostalCodeSet = buildSet(requiresPostalCode);
    const requiredStateSet = buildSet(requiresState);

    const newRules = {
        address1: [
            notEmpty,
            lengthAtLeast(1),
            lengthAtMost(255)
        ],
        address2: [
            optionalRule([
                lengthAtMost(255)
            ])],
        city: [
            notEmpty,
            lengthAtLeast(2),
            lengthAtMost(120)
        ],
        postalCode: [
            optionalRule([
                lengthAtLeast(1),
                lengthAtMost(20),
                validPostalCode
            ])],
        country: [
            notEmpty
        ],
        stateProvince: [
            optionalRule([
                lengthAtLeast(2),
                lengthAtMost(120)
            ])]
    };
    const result = newRules;

    if (specificPostalCodeSet.has(country)) {
        result.postalCode.push(notEmpty);
        // eslint-disable-next-line default-case
        switch (country) {
        case 'US':
            result.postalCode = [validUSPostalCode];
            break;
        case 'CA':
            result.postalCode = [validCAPostalCode];
            break;
        case 'GB':
            result.postalCode = [validGBPostalCode];
            break;
        }
        result.postalCode.push(lengthAtLeast(1));
        result.postalCode.push(lengthAtMost(20));
    } else if (requiresPostalCodeSet.has(country)) {
        result.postalCode = [
            notEmpty,
            validPostalCode,
            lengthAtLeast(1),
            lengthAtMost(20)
        ];
    }

    if (requiredStateSet.has(country)) {
        result.stateProvince = [
            notEmpty,
            lengthAtLeast(1),
            lengthAtMost(120)
        ];
    }

    if (latinOnly) {
        // Each one has a different validation so we can
        // put the localized field name in the error message.
        result.address1.unshift(validStreetAddress);
        result.address2 = [
            optionalRule([
                validStreetAddress,
                lengthAtMost(255)

            ])];

        result.city.unshift(validCity);

        if (requiredStateSet.has(country)) {
            result.stateProvince.unshift(validState);
        } else {
            result.stateProvince = [optionalRule([
                validState,
                lengthAtLeast(1),
                lengthAtMost(120)
            ])];
        }
    }

    return result;
};

// these rules comply with Salesforce limitations
export const getPcoRules = (country, latinOnly) => {
    const specificPostalCodeSet = buildSet(specificPostalCode);
    const requiresPostalCodeSet = buildSet(requiresPostalCode);
    const requiredStateSet = buildSet(requiresState);

    const defaultRules = {
        address1: [
            notEmpty,
            lengthAtLeast(3),
            lengthAtMost(200)
        ],
        address2: [
            optionalRule([
                lengthAtMost(50)
            ])],
        city: [
            notEmpty,
            lengthAtLeast(2),
            lengthAtMost(40)
        ],
        postalCode: [
            optionalRule([
                lengthAtLeast(3),
                lengthAtMost(12),
                validPostalCode
            ])],
        country: [
            notEmpty
        ],
        stateProvince: [
            optionalRule([
                lengthAtLeast(2),
                lengthAtMost(60)
            ])]
    };
    const result = defaultRules;

    if (specificPostalCodeSet.has(country)) {
        result.postalCode.push(notEmpty);
        // eslint-disable-next-line default-case
        switch (country) {
        case 'US':
            result.postalCode = [validUSPostalCode];
            break;
        case 'CA':
            result.postalCode = [validCAPostalCode];
            break;
        case 'GB':
            result.postalCode = [validGBPostalCode];
            break;
        }
        result.postalCode.push(lengthAtLeast(3));
        result.postalCode.push(lengthAtMost(12));
    } else if (requiresPostalCodeSet.has(country)) {
        result.postalCode = [
            notEmpty,
            validPostalCode,
            lengthAtLeast(3),
            lengthAtMost(12)
        ];
    }

    if (requiredStateSet.has(country)) {
        result.stateProvince = [
            notEmpty,
            lengthAtLeast(2),
            lengthAtMost(60)
        ];
    }

    if (latinOnly) {
        // Each one has a different validation so we can
        // put the localized field name in the error message.
        result.address1.unshift(validStreetAddress);
        result.address2 = [
            optionalRule([
                validStreetAddress,
                lengthAtMost(50)

            ])];

        result.city.unshift(validCity);

        if (requiredStateSet.has(country)) {
            result.stateProvince.unshift(validState);
        } else {
            result.stateProvince = [optionalRule([
                validState,
                lengthAtLeast(2),
                lengthAtMost(60)
            ])];
        }
    }

    return result;
};

// eslint-disable-next-line arrow-body-style
export const getRulesByCountry = (country, latinOnly, vrboRules = false) => {
    return vrboRules ? getVrboRules(country, latinOnly) : getPcoRules(country, latinOnly);
};

export default {
    getRulesByCountry
};
